/**
 * Oczekujące zaproszenia na liście pracowników (Fala 6, grupa 32, D99).
 *
 * Makieta listy zaproszeń nie zna, ale bez niej zaproszenie wystawione
 * i zapomniane blokowało adres: nowe odbijało się od „oczekującego", a jedyną
 * drogą odwołania było API. Kodu tu nie ma — w bazie leży wyłącznie jego skrót,
 * więc karta daje to, co wolno: kto, na jaki adres, do kiedy — i „Odwołaj".
 * Nowy kod wystawia modal „Zaproś pracownika".
 */
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, StatusPill, useToast } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { api, apiToday, type InvitationListItem } from '../../api/client'
import styles from './PendingInvitations.module.css'

export function PendingInvitations() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.invitations(),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInvitation(id),
    onSuccess: () => {
      toast.show(t('employees.pendingInvitations.revoked'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: () => toast.show(t('employees.pendingInvitations.revokeError'), { tone: 'danger' }),
  })

  const pending = (invitations.data?.items ?? []).filter(
    (item: InvitationListItem) => item.status === 'pending',
  )
  /* Pusta karta nie ma czego powiedzieć — pojawia się dopiero z pierwszym zaproszeniem. */
  if (pending.length === 0) return null

  const now = apiToday()

  return (
    <Card pad="lg" className={styles.card}>
      <h3 className={styles.title}>{t('employees.pendingInvitations.title')}</h3>
      <p className={styles.lead}>{t('employees.pendingInvitations.lead')}</p>
      <ul className={styles.list}>
        {pending.map((item) => {
          /* Po terminie, choć jeszcze „pending": blokuje adres, dopóki go nikt nie odwoła. */
          const expired = new Date(item.expiresAt) <= now
          const date = formatDate(new Date(item.expiresAt), 'dot')
          return (
            <li key={item.id} className={styles.row}>
              <span className={styles.person}>
                <span className={styles.name}>
                  {item.firstName} {item.lastName}
                </span>
                <span className={styles.meta}>
                  {item.email}
                  {item.position ? ` · ${item.position}` : ''}
                </span>
              </span>
              <StatusPill tone={expired ? 'neutral' : 'warning'}>
                {t(
                  expired
                    ? 'employees.pendingInvitations.expired'
                    : 'employees.pendingInvitations.validUntil',
                  { date },
                )}
              </StatusPill>
              <Button
                variant="secondary"
                size="sm"
                aria-label={t('employees.pendingInvitations.revokeLabel', { email: item.email })}
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(item.id)}
              >
                {t('employees.pendingInvitations.revoke')}
              </Button>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
